import * as yup from "yup";

import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Slide,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { cyan, red } from "@mui/material/colors";
import {
  useAddNewLanguageMutation,
  useDeleteCvLanguageMutation,
  useEditCvLanguageMutation,
} from "../../../lib/cvsSlice";

import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LanguageIcon from "@mui/icons-material/Language";
import SaveIcon from "@mui/icons-material/Save";
import { Stack } from "@mui/material";
import { languagesNames } from "config";
import useFormPersist from "react-hook-form-persist";
import { yupResolver } from "@hookform/resolvers/yup";

const cvSchema = yup.object().shape({
  language: yup.string().required("Language is required"),
  proficiency: yup.string().required(),
  otherLanguage: yup.string(),
  excludeInCv: yup.boolean(),
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Languages = ({ langs, cvId }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState();
  const [languages, setLanguages] = useState([]);
  const [languagesState, setLanguagesState] = useState(langs);
  const [langId, setLangId] = useState();
  const [languageElmnt, setLanguageElement] = useState(null);
  const [localExcludeInCv, setLocalExcludeInCv] = useState({});

  const handleOpen = (mode) => {
    setOpen(true);
    setMode(mode);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(cvSchema),
    defaultValues: {
      language: "",
      proficiency: "",
      otherLanguage: "",
      excludeInCv: false,
    },
  });

  const [addNewLanguage] = useAddNewLanguageMutation();
  const submitResults = async (e) => {
    // console.log('Submitting results:', e);

    const newLang = e;
    try {
      await addNewLanguage({ cvId, langs: [newLang] }).unwrap();
      handleClose();
      langs.push(newLang);
      toast.success("Language added successfully");
    } catch (error) {
      if (error.data?.message === "Language already exists.") {
        toast.error("Language already exists.");
      } else {
        toast.error("Failed to add language.");
      }
    }
  };

  const [deleteCvLanguage] = useDeleteCvLanguageMutation();

  const handleRemove = async (item) => {
    await deleteCvLanguage({ cvId, langId: item }).unwrap();
    const updatedCvLanguages = languagesState.filter(
      (lang) => lang.id !== item
    );
    setLanguagesState(updatedCvLanguages);
    langs.splice(
      langs.findIndex((lang) => lang._id === item),
      1
    );
    setLanguagesState([...langs]);
    toast.info("Language removed successfully");
  };

  const [editCvLanguage] = useEditCvLanguageMutation();
  const handleEdit = (langId, mode) => {
    setOpen(true);
    setMode(mode);
    setLangId(langId);
  };

  useFormPersist("languages", {
    watch,
    setValue,
    storage: window.localStorage, // default window.sessionStorage
  });

  useEffect(() => {
    const data = localStorage.getItem("languages");
    if (data !== null) setLanguages(JSON.parse(data));
  }, []);

  useEffect(() => {
    localStorage.setItem("languages", JSON.stringify(languages));
  }, [languages]);

  useEffect(() => {
    const initialLocalExcludeInCv = {};
    langs.forEach((lang) => {
      const savedState = localStorage.getItem(`excludeInCv-${lang._id}`);
      initialLocalExcludeInCv[lang._id] = savedState === "true";
    });

    setLocalExcludeInCv(initialLocalExcludeInCv);
  }, [langs]);

  const handleInputChange = (event, langId, fieldName) => {
    const languageElement = langs.find((lang) => lang._id === langId);
    languageElement[fieldName] = event.target.value;

    const updatedLang = langs.map((lang) => {
      if (lang._id === langId) {
        return { ...lang, [fieldName]: event.target.value };
      }
      return lang;
    });
    setLanguagesState(updatedLang);
  };

  const handleToggleChange = async (
    newExcludeInCv,
    langId,
    fieldName,
    updatedValue
  ) => {
    try {
      const updatedLangs = langs.map((lang) => {
        if (lang._id === langId) {
          return {
            ...lang,
            [fieldName]: updatedValue,
          };
        }
        return lang;
      });

      setLocalExcludeInCv((prevLocalExcludeInCv) => ({
        ...prevLocalExcludeInCv,
        [langId]: newExcludeInCv,
      }));
      localStorage.setItem(`excludeInCv-${langId}`, newExcludeInCv.toString());

      await editCvLanguage({
        cvId: cvId,
        langId: langId,
        langs: updatedLangs.map((lang) => ({
          ...lang,
          excludeInCv: newExcludeInCv,
        })),
      });

      setLanguagesState(updatedLangs);
    } catch (error) {
      console.error("Error updating toggle:", error);
    }
  };

  //Edit mode functionality
  const languageElement = langs.find((lang) => lang._id === langId);
  const renderSingleElement = () => {
    return (
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        style={{ marginTop: "10px" }}
      >
        <Grid item xs={12}>
          <Controller
            name="language"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label="Language"
                  select
                  fullWidth
                  SelectProps={{
                    MenuProps: {
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "center",
                      },
                    },
                  }}
                  error={!!errors.language}
                  helperText={errors?.language ? errors.language?.message : ""}
                  value={languageElement.language}
                  onChange={(e) => handleInputChange(e, langId, "language")}
                >
                  {languagesNames &&
                    languagesNames.map((language) => (
                      <MenuItem key={language.label} value={language.value}>
                        {language.value}
                      </MenuItem>
                    ))}
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
                {field.value === "other" && (
                  <TextField
                    label="Enter language"
                    {...register("otherLanguage", { required: true })}
                    error={!!errors.otherLanguage}
                    helperText={
                      errors?.otherLanguage ? "Please enter a language" : ""
                    }
                    fullWidth
                    value={languageElement.otherLanguage}
                    onChange={(e) =>
                      handleInputChange(e, langId, "otherLanguage")
                    }
                  />
                )}
              </>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="proficiency"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Proficiency"
                fullWidth
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                  },
                }}
                error={!!errors.proficiency}
                helperText={
                  errors?.proficiency ? errors.proficiency?.message : ""
                }
                value={languageElement.proficiency}
                onChange={(e) => handleInputChange(e, langId, "proficiency")}
              >
                <MenuItem value="Limited work-related use">
                  Limited work-related use
                </MenuItem>
                <MenuItem value="Good work-related use">
                  Good work-related use
                </MenuItem>
                <MenuItem value="Fluent or native language">
                  Fluent or native language
                </MenuItem>
              </TextField>
            )}
          />
        </Grid>
      </Grid>
    );
  };

  const submitEditResults = async () => {
    await editCvLanguage({
      cvId,
      langId,
      langs: [languageElement],
    }).unwrap();
    handleClose();
    toast.info("Education updated successfully");
  };

  return (
    <div id="Languages">
      <ToastContainer autoClose={1500} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper style={{ marginTop: "20px", padding: "15px" }}>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              style={{ marginTop: "5px" }}
            >
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={9}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      component="div"
                      sx={{ p: 0, pb: 0 }}
                    >
                      Languages &nbsp;
                      <Tooltip
                        disableFocusListener
                        disableTouchListener
                        title="This section is mandatory"
                      >
                        <span style={{ color: "red" }}>*</span>
                      </Tooltip>
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="add"
                      onClick={() => handleOpen("add")}
                    >
                      <AddIcon color="primary" />
                    </IconButton>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleOpen("edit")}
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {langs?.map((lang) => (
                  <List key={lang._id} style={{ padding: "0px" }}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar style={{ backgroundColor: cyan[700] }}>
                          <LanguageIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        style={{ paddingLeft: "10px" }}
                        primary={
                          <Typography variant="subtitle2" components="div">
                            Language: {lang.language}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" display="block">
                            Proficiency: {lang.proficiency}
                            <Typography variant="caption" display="block">
                              Exclude in cv: {lang.excludeInCv ? "Yes" : "No"}
                            </Typography>
                          </Typography>
                        }
                      />

                      <Grid
                        item
                        xs={12}
                        sm={3}
                        style={{ textAlign: "right", paddingRight: "40px" }}
                      >
                        <FormControl>
                          <FormHelperText error={!!errors.excludeInCv}>
                            {errors?.excludeInCv
                              ? errors.excludeInCv?.message
                              : ""}
                          </FormHelperText>
                          <Controller
                            key={lang._id}
                            name={`excludeInCv-${lang._id}`}
                            control={control}
                            render={({
                              field: { onChange, value, ...field },
                            }) => (
                              <Tooltip title="This will exclude the section from the CV">
                                <FormControlLabel
                                  style={{ margin: "0px" }}
                                  control={
                                    <Switch
                                      onChange={(e) => {
                                        const newExcludeInCv = e.target.checked;

                                        // Update the local state for the specific certificate immediately
                                        setLocalExcludeInCv(
                                          (prevLocalExcludeInCv) => ({
                                            ...prevLocalExcludeInCv,
                                            [lang._id]: newExcludeInCv,
                                          })
                                        );

                                        handleToggleChange(
                                          newExcludeInCv,
                                          lang._id,
                                          "excludeInCv"
                                        );

                                        console.log(
                                          "Toggle updated successfully"
                                        );
                                      }}
                                      checked={
                                        localExcludeInCv[lang._id] || false
                                      }
                                      {...field}
                                      ref={null}
                                    />
                                  }
                                />
                              </Tooltip>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </ListItem>
                  </List>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        {mode === "add" && (
          <form
            autoComplete="off"
            onSubmit={handleSubmit(() => submitResults(watch()))}
          >
            <DialogTitle>Add a New Language </DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                style={{ marginTop: "10px" }}
              >
                <Grid item xs={12}>
                  <Controller
                    name="language"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <>
                        <TextField
                          {...field}
                          label="Language"
                          select
                          fullWidth
                          SelectProps={{
                            MenuProps: {
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "center",
                              },
                            },
                          }}
                          error={!!errors.language}
                          helperText={
                            errors?.language ? errors.language?.message : ""
                          }
                          style={{ paddingBottom: "20px" }}
                        >
                          {languagesNames &&
                            languagesNames.map((language) => (
                              <MenuItem
                                key={language.label}
                                value={language.value}
                              >
                                {language.value}
                              </MenuItem>
                            ))}
                          <MenuItem value="other">Other</MenuItem>
                        </TextField>
                        {field.value === "other" && (
                          <TextField
                            label="Enter language"
                            {...register("otherLanguage", { required: true })}
                            error={!!errors.otherLanguage}
                            helperText={
                              errors?.otherLanguage
                                ? "Please enter a language"
                                : ""
                            }
                            fullWidth
                          />
                        )}
                      </>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="proficiency"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Proficiency"
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        value={field.value}
                        error={!!errors.proficiency}
                        helperText={
                          errors?.proficiency ? errors.proficiency?.message : ""
                        }
                      >
                        <MenuItem value="Limited work-related use">
                          Limited work-related use
                        </MenuItem>
                        <MenuItem value="Good work-related use">
                          Good work-related use
                        </MenuItem>
                        <MenuItem value="Fluent or native language">
                          Fluent or native language
                        </MenuItem>
                      </TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl>
                    <FormHelperText error={!!errors.excludeInCv}>
                      {errors?.excludeInCv ? errors.excludeInCv?.message : ""}
                    </FormHelperText>
                    <Controller
                      name="excludeInCv"
                      control={control}
                      render={({ field: { onChange, value, ...field } }) => (
                        <Tooltip title="This is exclude the section from the cv">
                          <FormControlLabel
                            label="Exclude in Cv"
                            style={{ margin: "0px" }}
                            control={
                              <Switch
                                onChange={onChange}
                                checked={value}
                                {...field}
                                ref={null}
                              />
                            }
                          />
                        </Tooltip>
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                style={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
        {mode === "edit" && (
          <form>
            <DialogTitle>Edit/Remove Language</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                style={{ marginTop: "5px" }}
              >
                <Grid item xs={12}>
                  {langs?.map((lang) => (
                    <List key={lang._id}>
                      <ListItem
                        style={{ paddingLeft: "10px" }}
                        secondaryAction={
                          <Stack alignItems="right" direction="row">
                            <IconButton
                              aria-label="edit"
                              onClick={() => handleEdit(lang._id, "editnew")}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleRemove(lang._id)}
                              style={{ color: red[400] }}
                            >
                              <DeleteIcon style={{ color: red[400] }} />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          style={{ paddingLeft: "10px" }}
                          primary={
                            <Typography variant="subtitle2" component="div">
                              Language: {lang.language}
                            </Typography>
                          }
                          secondary={
                            <div>
                              <Typography variant="caption" display="block">
                                Proficiency: {lang.proficiency}
                                <Typography variant="caption" display="block">
                                  Exclude in cv:{" "}
                                  {lang.excludeInCv ? "Yes" : "No"}
                                </Typography>
                              </Typography>
                            </div>
                          }
                        />
                      </ListItem>
                    </List>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                style={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                      disabled
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
        {mode === "editnew" && (
          <form
            autoComplete="off"
            onSubmit={() => {
              submitEditResults();
            }}
          >
            <DialogTitle>Edit Languages</DialogTitle>
            <DialogContent dividers={true}>
              {renderSingleElement()}
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                style={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
      </Dialog>
    </div>
  );
};

export default Languages;